/**
 * Los tres paneles, uno por rol.
 *
 * El del alumnado **es** el campo del PIN, no una pantalla con un enlace al PIN. Antes era un
 * título y una frase, sin un solo enlace para llegar a jugar: la pantalla existía y funcionaba,
 * pero era inalcanzable desde la interfaz. Aquí no hay tarjetas, ni saludo, ni nada por encima
 * del campo, porque lo único que un alumno viene a hacer es teclear cuatro cifras.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SessionProfile } from '@clica/shared';
import { EntradaPin } from '../components/sistema/entrada-pin.js';
import { Boton } from '../components/sistema/boton.js';
import { Glifo } from '../components/sistema/glifo.js';
import { Marco } from '../components/sistema/marco.js';
import { Rotulo } from '../components/sistema/rotulo.js';

/** Pone el título de la pestaña, que era «Clica» en las quince pantallas. */
function useTituloDePestana(titulo: string): void {
  const { t } = useTranslation();
  useEffect(() => {
    document.title = `${titulo} · ${t('app.name')}`;
  }, [titulo, t]);
}

export function PanelAlumno({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  useTituloDePestana(t('game.join.title'));

  return (
    <Marco profile={profile}>
      <main
        id="contenido"
        className="mx-auto flex w-full max-w-md flex-col justify-center gap-8 p-6"
        // Lo que quede de pantalla bajo la cabecera es para el PIN y nada más.
        style={{ minHeight: 'calc(100dvh - 5rem)' }}
      >
        <div className="text-center">
          <h1 className="font-display text-h2 font-extrabold text-balance">
            {t('game.join.title')}
          </h1>
          <p className="text-body text-text-dim mt-2">{t('game.join.lead')}</p>
        </div>

        <EntradaPin />
      </main>
    </Marco>
  );
}

/**
 * Lo que ve el profesorado al entrar.
 *
 * Antes era un `h1` y un único botón. Ahora son las tres cosas que se hacen de verdad —material,
 * partidas y traer un cuestionario de fuera—, cada una con su descripción, porque quien entra por
 * primera vez no tiene por qué saber qué es una «asignatura» en esta aplicación.
 */
function ZonaProfesor(): React.JSX.Element {
  const { t } = useTranslation();

  /*
   * Dos accesos y los dos existen.
   *
   * Había un tercero, «Partidas», que apuntaba a una ruta que el enrutador no declara: caía en la
   * pantalla de «esto no existe». El histórico vive ahora dentro de cada cuestionario, que es
   * donde se busca, y en su lugar está lo que de verdad falta desde aquí: entrar con un código.
   * El profesorado también juega —para probar un cuestionario antes de la clase, o desde su
   * propio móvil mientras proyecta desde el portátil— y hasta ahora tenía que escribir `/join` a
   * mano.
   */
  const accesos = [
    {
      a: '/asignaturas',
      titulo: t('subjects.title'),
      pie: t('panel.teacher.subjectsLead'),
      principal: true,
    },
    {
      a: '/join',
      titulo: t('game.join.title'),
      pie: t('panel.teacher.joinLead'),
      principal: false,
    },
  ] as const;

  return (
    <main id="contenido" className="mx-auto w-full max-w-3xl p-6">
      <h1 className="entra font-display text-h1 font-extrabold text-balance">
        {t('panel.teacher.title')}
      </h1>
      <p
        className="entra text-lead text-text-dim mt-2"
        style={{ '--retraso': '60ms' } as React.CSSProperties}
      >
        {t('panel.teacher.lead')}
      </p>

      <ul className="mt-8 flex flex-col gap-4">
        {accesos.map((acceso, indice) => (
          <li
            key={acceso.a}
            className="entra"
            style={{ '--retraso': `${120 + indice * 70}ms` } as React.CSSProperties}
          >
            <Link
              to={acceso.a}
              className={[
                'rounded-card flex h-full items-center justify-between gap-4 border-2 p-5',
                'ease-hard transition-transform duration-(--dur-state) hover:-translate-y-0.5',
                acceso.principal
                  ? 'border-edge bg-hivis text-on-hivis'
                  : 'border-line bg-surface hover:bg-surface-hi',
              ].join(' ')}
            >
              <span className="flex flex-col gap-1">
                <span className="font-display text-h3 font-bold">{acceso.titulo}</span>
                <span className={acceso.principal ? 'text-body' : 'text-body text-text-dim'}>
                  {acceso.pie}
                </span>
              </span>
              <Glifo nombre="reanudar" size={26} />
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}

export function PanelProfesor({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  useTituloDePestana(t('panel.teacher.title'));
  return (
    <Marco profile={profile}>
      <ZonaProfesor />
    </Marco>
  );
}

/**
 * Panel de administración, con el conmutador entre vista de administración y de profesor.
 *
 * Es un cambio de **vista**, no de sesión ni de rol: el rol almacenado no se toca, y el permiso
 * se vuelve a comprobar en el servidor en cada petición (§3.3 de la especificación).
 */
export function PanelAdmin({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const [vista, setVista] = useState<'admin' | 'teacher'>('admin');
  useTituloDePestana(t('panel.admin.title'));

  return (
    <Marco profile={profile}>
      <nav aria-label={t('panel.admin.viewSwitch')} className="mx-auto max-w-5xl px-6 pt-6">
        <ul className="inline-flex gap-2">
          {(['admin', 'teacher'] as const).map((opcion) => (
            <li key={opcion}>
              <Boton
                // La vista activa se distingue por relleno, borde y peso, no solo por color, y
                // además lo dice `aria-current` para quien no ve ninguno de los tres.
                variante={vista === opcion ? 'primaria' : 'secundaria'}
                aria-current={vista === opcion ? 'page' : undefined}
                onClick={() => setVista(opcion)}
              >
                {opcion === 'admin' ? t('panel.admin.viewAdmin') : t('panel.admin.viewTeacher')}
              </Boton>
            </li>
          ))}
        </ul>
      </nav>

      {vista === 'admin' ? (
        <main id="contenido" className="mx-auto w-full max-w-5xl p-6">
          <h1 className="font-display text-h1 font-extrabold text-balance">
            {t('panel.admin.title')}
          </h1>
          <p className="text-lead text-text-dim mt-2">{t('panel.admin.lead')}</p>

          <ul className="mt-8 grid gap-4 sm:grid-cols-2">
            <li>
              <Link
                to="/admin/usuarios"
                className="rounded-card border-line bg-surface hover:bg-surface-hi flex h-full flex-col gap-2 border-2 p-5"
              >
                <span className="font-display text-h3 font-bold">{t('panel.admin.usersLink')}</span>
                <span className="text-body text-text-dim">{t('panel.admin.usersLead')}</span>
              </Link>
            </li>
          </ul>

          <Rotulo className="mt-8 block">{t('panel.admin.roleNote')}</Rotulo>
        </main>
      ) : (
        <ZonaProfesor />
      )}
    </Marco>
  );
}
